<template>
  <div class="px-6 py-4" style="max-height: 80vh; overflow-y: auto;">
    <!-- Filters Section -->
    <div class="mb-4 bg-gray-50 dark:bg-dark-700 p-3 rounded-lg border border-gray-200 dark:border-dark-600">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <!-- Search -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">Search</label>
          <input
            v-model="filters.search"
            type="text"
            placeholder="Search by client name..."
            class="w-full rounded-md border-gray-300 dark:border-dark-500 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            @input="debouncedFetch"
          />
        </div>
        
        <!-- Sort By -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">Sort By</label>
          <select
            v-model="filters.sort"
            class="w-full rounded-md border-gray-300 dark:border-dark-500 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            @change="fetchData"
          >
            <option value="amount_high">Outstanding Amount (High to Low)</option>
            <option value="amount_low">Outstanding Amount (Low to High)</option>
            <option value="orders_high">Pending Orders (High to Low)</option>
            <option value="orders_low">Pending Orders (Low to High)</option>
            <option value="recent_payment">Recent Payment First</option>
            <option value="oldest_payment">Oldest Payment First</option>
          </select>
        </div>
        
        <!-- Status Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">Amount Range</label>
          <select
            v-model="filters.amountRange"
            class="w-full rounded-md border-gray-300 dark:border-dark-500 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            @change="fetchData"
          >
            <option value="all">All Amounts</option>
            <option value="critical">Critical (₱100,000+)</option>
            <option value="high">High (₱50,000 - ₱100,000)</option>
            <option value="medium">Medium (₱10,000 - ₱50,000)</option>
            <option value="low">Low (Below ₱10,000)</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Summary Stats -->
    <div class="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
      <div class="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
        <div class="text-sm text-blue-600 dark:text-blue-400">Total Clients</div>
        <div class="text-2xl font-bold text-blue-900 dark:text-blue-100">{{ totalItems }}</div>
      </div>
      <div class="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg border border-orange-200 dark:border-orange-800">
        <div class="text-sm text-orange-600 dark:text-orange-400">Total Pending Orders</div>
        <div class="text-2xl font-bold text-orange-900 dark:text-orange-100">{{ totalPendingOrders }}</div>
      </div>
      <div class="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800">
        <div class="text-sm text-red-600 dark:text-red-400">Total Outstanding</div>
        <div class="text-2xl font-bold text-red-900 dark:text-red-100">₱{{ formatPrice(totalOutstanding) }}</div>
      </div>
    </div>

    <!-- Results Table -->
    <div class="overflow-x-auto border border-gray-200 dark:border-dark-600 rounded-lg shadow-sm mb-4">
      <table class="min-w-full divide-y divide-gray-200 dark:divide-dark-600">
        <thead class="bg-gray-50 dark:bg-dark-700">
          <tr>
            <th scope="col" class="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Client Name</th>
            <th scope="col" class="px-6 py-2 text-center text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Pending Orders</th>
            <th scope="col" class="px-6 py-2 text-right text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Outstanding Amount</th>
            <th scope="col" class="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Last Payment</th>
            <th scope="col" class="px-6 py-2 text-center text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-600">
          <template v-if="loading">
            <tr>
              <td colspan="5" class="px-6 py-3 text-center text-sm text-gray-500 dark:text-dark-400">Loading client payment data...</td>
            </tr>
          </template>
          <template v-else-if="clients.length === 0">
            <tr>
              <td colspan="5" class="px-6 py-3 text-center text-sm text-gray-500 dark:text-dark-400">No client payment records found</td>
            </tr>
          </template>
          <template v-else>
            <tr v-for="client in clients" :key="client.id" class="hover:bg-gray-50 dark:hover:bg-dark-700">
              <td class="px-6 py-2 whitespace-nowrap">
                <div class="text-sm font-medium text-gray-900 dark:text-dark-100">
                  {{ client.name }}
                </div>
                <div v-if="client.companyName" class="text-xs text-gray-500 dark:text-dark-400">
                  {{ client.companyName }}
                </div>
              </td>
              <td class="px-6 py-2 whitespace-nowrap text-center">
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                      :class="getPendingOrdersClass(client.pendingOrders)">
                  {{ client.pendingOrders }}
                </span>
              </td>
              <td class="px-6 py-2 whitespace-nowrap text-right">
                <div class="text-sm font-medium" :class="getOutstandingClass(client.outstanding)">
                  ₱{{ formatPrice(client.outstanding) }}
                </div>
              </td>
              <td class="px-6 py-2 whitespace-nowrap">
                <div class="text-sm text-gray-500 dark:text-dark-400">{{ client.lastPayment }}</div>
              </td>
              <td class="px-6 py-2 whitespace-nowrap text-center text-sm text-gray-500 dark:text-dark-400">
                <button 
                  @click="viewClientDetails(client)" 
                  class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                  title="View Client Orders"
                >
                  <i class="fas fa-eye"></i>
                </button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="flex items-center justify-between border-t border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-4 py-3 sm:px-6">
      <div class="flex flex-1 justify-between sm:hidden">
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="relative inline-flex items-center rounded-md border border-gray-300 dark:border-dark-500 bg-white dark:bg-dark-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-700"
          :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
        >
          Previous
        </button>
        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-dark-500 bg-white dark:bg-dark-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-700"
          :class="{ 'opacity-50 cursor-not-allowed': currentPage === totalPages }"
        >
          Next
        </button>
      </div>
      <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p class="text-sm text-gray-700 dark:text-dark-200">
            Showing <span class="font-medium">{{ totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1 }}</span> to
            <span class="font-medium">{{ Math.min(currentPage * perPage, totalItems) }}</span> of
            <span class="font-medium">{{ totalItems }}</span> results
          </p>
        </div>
        <div>
          <nav class="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <button
              @click="goToPage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 dark:text-dark-400 ring-1 ring-inset ring-gray-300 dark:ring-dark-500 hover:bg-gray-50 dark:hover:bg-dark-700 focus:z-20 focus:outline-offset-0"
              :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
            >
              <span class="sr-only">Previous</span>
              <i class="fas fa-chevron-left h-5 w-5"></i>
            </button>
            <button
              v-for="page in displayedPages"
              :key="page"
              @click="typeof page === 'number' ? goToPage(page) : null"
              :class="[
                typeof page === 'number' 
                  ? (page === currentPage
                      ? 'relative z-10 inline-flex items-center bg-blue-600 px-4 py-2 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                      : 'relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 dark:text-dark-100 ring-1 ring-inset ring-gray-300 dark:ring-dark-500 hover:bg-gray-50 dark:hover:bg-dark-700 focus:z-20 focus:outline-offset-0')
                  : 'relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-500 dark:text-dark-400 cursor-default'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="goToPage(currentPage + 1)"
              :disabled="currentPage === totalPages"
              class="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 dark:text-dark-400 ring-1 ring-inset ring-gray-300 dark:ring-dark-500 hover:bg-gray-50 dark:hover:bg-dark-700 focus:z-20 focus:outline-offset-0"
              :class="{ 'opacity-50 cursor-not-allowed': currentPage === totalPages }"
            >
              <span class="sr-only">Next</span>
              <i class="fas fa-chevron-right h-5 w-5"></i>
            </button>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useApi } from '@/composables/useApi';
import { useToast } from '@/composables/useToast';
import debounce from 'lodash/debounce';

const api = useApi();
const toast = useToast();

// Emit events
const emit = defineEmits(['view-client']);

// State
const clients = ref([]);
const loading = ref(false);
const filters = ref({
  search: '',
  sort: 'amount_high',
  amountRange: 'all'
});
const currentPage = ref(1);
const perPage = ref(10);
const totalItems = ref(0);
const totalPendingOrders = ref(0);
const totalOutstanding = ref(0);

// Computed properties
const totalPages = computed(() => {
  return Math.ceil(totalItems.value / perPage.value) || 1;
});

const displayedPages = computed(() => {
  const pages = [];
  const maxPagesToShow = 5;
  
  if (totalPages.value <= maxPagesToShow) {
    for (let i = 1; i <= totalPages.value; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);
    
    let startPage = Math.max(2, currentPage.value - 1);
    let endPage = Math.min(totalPages.value - 1, currentPage.value + 1);
    
    if (currentPage.value <= 2) {
      endPage = 3;
    } else if (currentPage.value >= totalPages.value - 1) {
      startPage = totalPages.value - 2;
    }
    
    if (startPage > 2) {
      pages.push('...');
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    if (endPage < totalPages.value - 1) {
      pages.push('...');
    }
    
    pages.push(totalPages.value);
  }
  
  return pages;
});

// Methods
const fetchData = async () => {
  try {
    loading.value = true;
    console.log('Fetching client payment data with filters:', filters.value);
    
    const params = {
      page: currentPage.value,
      limit: perPage.value,
      sort: filters.value.sort
    };
    
    if (filters.value.search && filters.value.search.trim() !== '') {
      params.search = filters.value.search.trim();
    }
    
    if (filters.value.amountRange && filters.value.amountRange !== 'all') {
      params.amountRange = filters.value.amountRange;
    }
    
    console.log('Request params:', params);
    const response = await api.get('/dashboard/client-payments', { params });
    
    if (response.data) {
      console.log('Response data:', response.data);
      
      // Handle both paginated and non-paginated responses
      if (response.data.data) {
        // Map backend response to component format
        clients.value = response.data.data.map(client => ({
          id: client.client_id,
          name: client.display_name || client.client_name || 'Unknown Client',
          companyName: client.company_name || null,
          pendingOrders: parseInt(client.pending_orders || 0),
          outstanding: parseFloat(client.outstanding_amount || 0),
          lastPayment: client.last_payment_date || 'N/A'
        }));
        totalItems.value = response.data.pagination?.total || response.data.data.length;
        
        // Update summary statistics from backend
        if (response.data.summary) {
          totalPendingOrders.value = parseInt(response.data.summary.totalPendingOrders || 0);
          totalOutstanding.value = parseFloat(response.data.summary.totalOutstandingAmount || 0);
        }
      } else if (Array.isArray(response.data)) {
        // Apply client-side filtering and sorting if needed
        let processedData = response.data;
        
        // Apply search filter
        if (params.search) {
          const searchLower = params.search.toLowerCase();
          processedData = processedData.filter(client => 
            client.client_name?.toLowerCase().includes(searchLower)
          );
        }
        
        // Apply amount range filter
        if (params.amountRange && params.amountRange !== 'all') {
          processedData = processedData.filter(client => {
            const amount = parseFloat(client.outstanding_amount || 0);
            switch (params.amountRange) {
              case 'critical': return amount >= 100000;
              case 'high': return amount >= 50000 && amount < 100000;
              case 'medium': return amount >= 10000 && amount < 50000;
              case 'low': return amount < 10000;
              default: return true;
            }
          });
        }
        
        // Apply sorting
        processedData.sort((a, b) => {
          const amountA = parseFloat(a.outstanding_amount || 0);
          const amountB = parseFloat(b.outstanding_amount || 0);
          const ordersA = parseInt(a.pending_orders || 0);
          const ordersB = parseInt(b.pending_orders || 0);
          const dateA = new Date(a.last_payment_date || 0);
          const dateB = new Date(b.last_payment_date || 0);
          
          switch (params.sort) {
            case 'amount_high': return amountB - amountA;
            case 'amount_low': return amountA - amountB;
            case 'orders_high': return ordersB - ordersA;
            case 'orders_low': return ordersA - ordersB;
            case 'recent_payment': return dateB - dateA;
            case 'oldest_payment': return dateA - dateB;
            default: return 0;
          }
        });
        
        // Calculate summary statistics for all data before pagination
        totalPendingOrders.value = processedData.reduce((sum, client) => 
          sum + parseInt(client.pending_orders || 0), 0);
        totalOutstanding.value = processedData.reduce((sum, client) => 
          sum + parseFloat(client.outstanding_amount || 0), 0);
        
        // Apply pagination
        totalItems.value = processedData.length;
        const startIndex = (currentPage.value - 1) * perPage.value;
        const endIndex = startIndex + perPage.value;
        
        clients.value = processedData.slice(startIndex, endIndex).map(client => ({
          id: client.client_id,
          name: client.display_name || client.client_name || 'Unknown Client',
          companyName: client.company_name || null,
          pendingOrders: parseInt(client.pending_orders || 0),
          outstanding: parseFloat(client.outstanding_amount || 0),
          lastPayment: client.last_payment_date || 'N/A'
        }));
      }
    }
  } catch (error) {
    console.error('Error fetching client payment data:', error);
    toast.error('Failed to load client payment status');
  } finally {
    loading.value = false;
  }
};

const debouncedFetch = debounce(() => {
  currentPage.value = 1;
  fetchData();
}, 300);

const goToPage = (page) => {
  if (typeof page === 'number' && page !== currentPage.value && page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    fetchData();
  }
};

const viewClientDetails = (client) => {
  emit('view-client', client);
};

const getPendingOrdersClass = (count) => {
  if (count >= 10) return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400';
  if (count >= 5) return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400';
  return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400';
};

const getOutstandingClass = (amount) => {
  if (amount >= 100000) return 'text-red-600 dark:text-red-400';
  if (amount >= 50000) return 'text-orange-600 dark:text-orange-400';
  if (amount >= 10000) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-green-600 dark:text-green-400';
};

const formatPrice = (price) => {
  return Number(price).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Setup
onMounted(() => {
  fetchData();
});

// Watch for filter changes
watch([() => filters.value.sort, () => filters.value.amountRange], () => {
  currentPage.value = 1;
  fetchData();
}, { deep: true });
</script>

